/**
 * Presentación de los estados de pago manual.
 *
 * Espeja `payments/constants.py` del backend, pero sólo para etiquetar y dar
 * color: el frontend nunca decide ni deduce el estado real de un pago. Si el
 * backend envía un estado desconocido, se muestra tal cual en tono neutro.
 */

export const PAYMENT_STATUS = {
    pendiente: {
        label: "Pendiente de pago",
        tone: "warning",
        help: "Todavía no nos consta el Bizum de este pedido.",
    },
    informado: {
        label: "Pago comunicado",
        tone: "info",
        help: "Nos has avisado de que ya has pagado. Falta que lo comprobemos a mano.",
    },
    confirmado: {
        label: "Pago confirmado",
        tone: "success",
        help: "Hemos comprobado el Bizum y el importe coincide.",
    },
    rechazado: {
        label: "Pago rechazado",
        tone: "error",
        help: "No hemos podido dar por bueno este pago.",
    },
    caducado: {
        label: "Código caducado",
        tone: "warning",
        help: "El código superó las 48 horas de validez.",
    },
    cancelado: {
        label: "Pago cancelado",
        tone: "neutral",
        help: "Este cobro ya no está activo.",
    },
    incidencia: {
        label: "Incidencia",
        tone: "error",
        help: "Hay algo que no cuadra y lo estamos revisando.",
    },
};

export const REFUND_STATUS = {
    devolucion_pendiente: { label: "Devolución pendiente", tone: "warning" },
    devuelto: { label: "Devuelto", tone: "success" },
    cancelada: { label: "Devolución cancelada", tone: "neutral" },
};

export const paymentStatusInfo = (status, fallbackLabel) =>
    PAYMENT_STATUS[status] ?? {
        label: fallbackLabel || status || "Sin estado",
        tone: "neutral",
        help: "",
    };

export const refundStatusInfo = (status, fallbackLabel) =>
    REFUND_STATUS[status] ?? { label: fallbackLabel || status, tone: "neutral" };

/**
 * Móvil español, con o sin prefijo +34 y con separadores opcionales.
 * Espeja `SPANISH_PHONE_VALIDATOR` del backend, que es quien decide de verdad.
 */
export const SPANISH_PHONE_RE = /^\s*(?:\+?34[\s-]?)?[6-9](?:[\s-]?\d){8}\s*$/;

/** Formatea un importe con su moneda en formato español. */
export const formatAmount = (value, currency = "EUR") =>
    new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: currency || "EUR",
    }).format(Number(value || 0));

/** Texto legible del tiempo que le queda a un código, o null si ya venció. */
export const remainingTimeLabel = (expiresAt) => {
    if (!expiresAt) return null;
    const diffMs = new Date(expiresAt).getTime() - Date.now();
    if (Number.isNaN(diffMs) || diffMs <= 0) return null;

    const hours = Math.floor(diffMs / 3_600_000);
    const minutes = Math.floor((diffMs % 3_600_000) / 60_000);
    if (hours >= 1) return `${hours} h ${minutes} min`;
    return `${minutes} min`;
};

/** Normaliza el error de axios a un texto mostrable en español. */
export const paymentApiError = (err, fallback) => {
    const data = err?.response?.data;
    if (!data) return fallback;
    if (typeof data === "string") return data;
    if (data.detail) return data.detail;
    const first = Object.entries(data)[0];
    if (!first) return fallback;
    const [, value] = first;
    return Array.isArray(value) ? value[0] : String(value);
};
